"use client"

import Link from 'next/link'
import { Clock, Heart, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import { BrandIcon } from '@/components/brand-icon'
import { Button } from '@/components/ui/button'
import { openCalendly } from '@/lib/calendly'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useStaggeredAnimation } from '@/hooks/use-animation-delay'
import { SimpleIconName } from '@/components/brand-icon'

const footerLinks = {
  quickLinks: [
    { name: 'Home', href: '/' },
    { name: 'About Dr. Arushi', href: '#about' },
    { name: 'Our Services', href: '#services' },
    // { name: 'Resume', href: '/resume' },
    { name: 'Contact Us', href: '#contact' },
  ],
  social: [
    { name: 'Instagram', icon: 'siInstagram' as SimpleIconName, href: 'https://www.instagram.com/drarushi.md', color: '#E4405F' },
    { name: 'Facebook', icon: 'siFacebook' as SimpleIconName, href: 'https://www.facebook.com/drarushi.md', color: '#1877F2' },
    { name: 'YouTube', icon: 'siYoutube' as SimpleIconName, href: 'https://www.youtube.com/@DrArushiSinghalJoshi', color: '#FF0000' },
  ],
}

export function Footer() {
  const { ref: footerRef, isIntersecting: isInView } = useIntersectionObserver({
    threshold: 0.1,
    once: true
  })
  const socialItems = useStaggeredAnimation(footerLinks.social.length, 260, 90)
  const linkItems = useStaggeredAnimation(footerLinks.quickLinks.length, 200, 80)

  return (
    <footer ref={footerRef} className="bg-navy text-white relative overflow-hidden w-full">
      {/* Animated background elements */}
      <div className={`absolute top-0 left-0 w-full h-full pointer-events-none`}>
        <div
          className="absolute top-20 right-20 w-64 h-64 bg-teal/5 rounded-full blur-3xl animate-pulse-soft"
          style={{ animationDuration: '8s' }}
        />
        <div
          className="absolute bottom-20 left-10 w-48 h-48 bg-peach/5 rounded-full blur-3xl animate-pulse-soft"
          style={{ animationDuration: '10s', animationDelay: '2s' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main footer content */}
        <div className="py-12 lg:py-16 grid gap-10 lg:gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand + CTA */}
          <div className={`min-w-0 ${isInView ? 'reveal visible' : 'reveal'}`}>
            <Link href="/" className="flex items-center gap-2 mb-6 group">
              <div className="w-10 h-10 rounded-full bg-teal flex items-center justify-center hover:scale-110 hover:rotate-3 transition-all duration-300">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-serif text-lg font-semibold group-hover:text-teal transition-colors">Dr. Arushi Singhal Joshi</p>
                <p className="text-xs text-white/60 -mt-1">MBBS, MD (Pediatrics), IBCLC</p>
              </div>
            </Link>
            <p className="text-white/70 leading-relaxed mb-6 text-sm">
              Compassionate pediatric care and lactation support in Jodhpur. Clear answers, unhurried consults, and evidence-based treatment.
            </p>

            <div className="flex flex-col gap-3">
              <a
                href="https://wa.me/919136486156?text=Hello%20Dr.%20Arushi,%20I%20would%20like%20to%20book%20an%20appointment"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button className="w-full bg-teal hover:bg-teal-dark text-white rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Schedule on WhatsApp
                </Button>
              </a>
              <Button
                variant="outline"
                className="w-full rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                onClick={() => {
                  void openCalendly()
                }}
              >
                Book Online
              </Button>
            </div>

            <div className="flex gap-3 mt-6">
              {footerLinks.social.map((social, index) => (
                <a
                  key={social.name}
                  href={social.href}
                  className={`
                    group w-10 h-10 rounded-full
                    bg-white/10
                    flex items-center justify-center
                    hover:bg-teal
                    hover:scale-115 hover:rotate-1
                    transition-all duration-300
                    ${socialItems[index] ? 'scale-in' : 'opacity-0 scale-0'}
                  `}
                  style={{
                    color: social.color,
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BrandIcon
                    name={social.icon}
                    size={social.name === 'X' ? 16 : 18}
                    className="text-white group-hover:text-current"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div
            className={`min-w-0 ${isInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '80ms' }}
          >
            <h4 className="font-semibold text-lg mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.quickLinks.map((link, index) => (
                <li
                  key={link.name}
                  className={`${linkItems[index] ? 'reveal visible' : 'reveal'}`}
                  style={{ transitionDelay: `${160 + index * 80}ms` }}
                >
                  <div className="hover:translate-x-1 transition-transform duration-300">
                    <Link
                      href={link.href}
                      className="text-white/70 hover:text-teal transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div
            className={`min-w-0 ${isInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '140ms' }}
          >
            <h4 className="font-semibold text-lg mb-5">Contact</h4>
            <ul className="space-y-4 text-white/70 text-sm">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                <span>
                  17E/546, Chopasni Housing Board,
                  <br />
                  Jodhpur, Rajasthan 342008
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                <a
                  href="tel:+919136486156"
                  className="hover:text-teal transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-teal/60"
                >
                  +91 91364 86156
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                <a
                  href="mailto:arushisinghaljoshi@gmail.com"
                  className="hover:text-teal transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-teal/60"
                >
                  arushisinghaljoshi@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div
            className={`min-w-0 ${isInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <h4 className="font-semibold text-lg mb-5">Clinic Hours</h4>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-teal" />
                <p className="text-sm font-semibold text-white">Appointments</p>
              </div>
              <div className="space-y-2 text-sm text-white/70">
                <p>
                  <span className="text-white/80 font-medium">Mon - Sat</span>
                  <span className="mx-2 text-white/30">•</span>
                  4:30 PM - 7:30 PM
                </p>
                <p>
                  <span className="text-white/80 font-medium">Sun</span>
                  <span className="mx-2 text-white/30">•</span>
                  11:00 AM - 1:00 PM
                </p>
              </div>
              <p className="mt-4 text-xs text-white/50">
                Please WhatsApp for the quickest scheduling.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className={`py-6 border-t border-white/10 flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-2 text-center`}
          style={{ animationDelay: '300ms' }}
        >
          <p className="text-white/60 text-sm">
            {new Date().getFullYear()} Dr. Arushi Singhal Joshi. All rights reserved.
          </p>
          <p className="text-white/50 text-xs">
            Designed for parents. Built with care.
          </p>
        </div>
      </div>
    </footer>
  )
}
